import { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, Box, CircularProgress, Alert,
} from '@mui/material';

/**
 * "Send login" — hands a referrer a link that lets them set their own password.
 *
 * (user 2026-10-31: "all old customer got referral, u all can share them referral link or
 *  give them login")
 *
 * The link is shown rather than emailed because most referrers have no email on file —
 * 590 of 2,201 customers do — and the person who introduced them is holding a phone with
 * their number in it. So: copy it, or open WhatsApp with the message already written.
 *
 * The LINK IS THE CREDENTIAL for seven days, which is why the wording says to send it to
 * that person and nobody else. Opening this dialog mints a fresh one and retires any
 * earlier unused link for the same account, so an old message in a chat stops working.
 */

const FONT = "'Poppins', sans-serif";

export default function ReferrerLoginLinkDialog({ open, onClose, loading, error, data }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data?.url || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard is blocked on an insecure origin and in some in-app browsers;
            // the field is selectable, so there is still a way through.
            setCopied(false);
        }
    };

    const waHref = () => {
        const digits = String(data?.phone || '').replace(/\D/g, '');
        const intl = digits.startsWith('60') ? digits : '60' + digits.replace(/^0/, '');
        const msg =
            `Hi ${data?.name || ''}, here is your nexwave login.\n\n` +
            `Username: ${data?.username || ''}\n` +
            `Set your password here: ${data?.url || ''}\n\n` +
            `The link works once and expires in 7 days.`;
        return `https://wa.me/${intl}?text=${encodeURIComponent(msg)}`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Send login</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B', mb: 2 }}>
                            Send this to {data?.name || 'them'} and nobody else — anyone holding the
                            link can set the password on this account. It works once and expires in
                            seven days, and any earlier link for them has just stopped working.
                        </Typography>

                        <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: '#64748B' }}>
                            USERNAME
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 600, mb: 2 }}>
                            {data?.username || '—'}
                        </Typography>

                        <TextField
                            fullWidth size="small" value={data?.url || ''}
                            InputProps={{ readOnly: true }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', fontFamily: FONT } }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily: FONT, textTransform: 'none' }}>Close</Button>
                <Button
                    onClick={copy}
                    disabled={!data?.url}
                    variant="outlined"
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600 }}
                >
                    {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button
                    component="a"
                    href={data?.phone ? waHref() : undefined}
                    target="_blank"
                    rel="noreferrer"
                    disabled={!data?.url || !data?.phone}
                    variant="contained"
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600 }}
                >
                    Send on WhatsApp
                </Button>
            </DialogActions>
        </Dialog>
    );
}
